import { forEach, get, isPlainObject } from 'lodash';

// Credential shapes that must never reach Sentry, derived from the route tables in
// packages/agent/src/routes.js and packages/client/src/routes.js.
//
// Every pattern stops at a quote, semicolon or whitespace as well as the obvious delimiter. A URL
// does not always arrive as a standalone string — it can be embedded in a breadcrumb message, an
// exception value or a serialised context — and a token bounded only by `&` or `/` runs past the
// closing quote and swallows the rest of the payload.
const BOUNDARY = '[^/?"\';\\s]';

// `?access_token=` on reset-password, `?token=` on /api/Agents/confirm and on every magic link.
// client-v2 re-appends `?token=` to each internal link, so the credential follows the user through
// the whole session rather than appearing once at entry.
//
// The leading group matches the start of the string as well as a delimiter, because a query string
// does not always arrive attached to its url: `event.request.query_string` is the search with the
// `?` already stripped, so a pattern anchored on the delimiter alone reads the same credential as
// absent in the very field that holds it bare.
const ACCESS_TOKEN_PARAM = new RegExp(`(^|[?&])access_token=[^&"';\\s]+`, 'gi');
const TOKEN_PARAM = new RegExp(`(^|[?&])token=[^&"';\\s]+`, 'gi');

// Path segments. A key-name scrubber cannot see any of these, because there is no key — the
// credential IS the path. Neither our own key-based rules nor Sentry's ingest scrubber catch them.
const CLIENT_ROUTE_TOKEN = new RegExp(`(/client/(?:login|forgot|forgotten|loggedOut|reset|portal|activate)/)${BOUNDARY}+`, 'gi');

// The agent app routes these off the root, with no prefix to key on.
const AGENT_ROUTE_TOKEN = new RegExp(`(/(?:login|forgot|forgotten|loggedOut|reset|register|registered|verified)/)${BOUNDARY}+`, 'gi');

// Verify takes both /verify/:token and /verify/:uid/:token. Only the last segment is the
// credential — the uid is an identifier worth keeping, so the optional middle group is put back.
const VERIFY_TOKEN = new RegExp(`(/verify/)((?:${BOUNDARY}+/)?)${BOUNDARY}+`, 'gi');

// Koedia's PushBookDesc signs nothing, so DIGITRIPS_PUSH_SECRET rides as the webhook's path segment.
const DIGITRIPS_PUSH_SECRET = new RegExp(`(/webhooks/digitrips/)${BOUNDARY}+`, 'gi');

// A bearer rides outside any URL when a header map is serialised into a breadcrumb or a context.
const BEARER_HEADER = /(authorization"?\s*[:=]\s*"?)(?:bearer\s+)?[^\s"',;]+/gi;

// Header names whose whole value is a credential, so no URL pattern can reach them. The masquerade
// pair is ours, which is why Sentry's own header handling does not know about it.
const CREDENTIAL_HEADER_NAMES = ['authorization', 'x-access-masq', 'x-access-masqfrom'];
const CREDENTIAL_HEADER = new RegExp(`^(?:${CREDENTIAL_HEADER_NAMES.join('|')})$`, 'i');

// The same credentials as OpenTelemetry span attributes, which is a second route with no header map
// to key on. The SDK prefixes the header name and rewrites each dash as an underscore, and the cookie
// header arrives split into one attribute per cookie name. A cookie name is LOWERCASED on the way in
// while a header name is not, so the cookie entries here are lower case and `MASQ_FROM` is `masq_from`.
//
// Sentry filters these itself, but only by substring: its deny list holds `auth`, so `authorization`
// arrives as `[Filtered]`, and it holds nothing that `x-access-masq` or a `MASQ` cookie contains, so
// those arrive whole. Both are listed here anyway — a name the SDK already filtered costs one set
// lookup, and the set stops being a guess about which substrings Sentry ships.
const SPAN_HEADER_PREFIX = 'http.request.header.';
const CREDENTIAL_SPAN_KEYS = new Set([
  ...CREDENTIAL_HEADER_NAMES.map(name => `${SPAN_HEADER_PREFIX}${name.replace(/-/g, '_')}`),
  `${SPAN_HEADER_PREFIX}cookie.token`,
  `${SPAN_HEADER_PREFIX}cookie.client_token`,
  `${SPAN_HEADER_PREFIX}cookie.masq`,
  `${SPAN_HEADER_PREFIX}cookie.masq_from`,
]);

// Endpoints whose REQUEST body is a password the user typed. Replay's `networkDetailAllowUrls` is a
// prefix match on /api and `networkCaptureBodies` defaults to true, so without a deny match the body
// lands in the recording. A password has no shape to key on — any string is a valid one — so the only
// thing that can stop it is refusing to capture the call at all.
//
// A minted access token is NOT here, even though several of these also answer with one. TOKEN_JSON_VALUE
// above covers every token by its shape, wherever it appears, which is what an endpoint list could not
// do: it missed a live token three times in one review round. Listing an endpoint twice would buy
// nothing and would cost the response detail that makes a replay worth recording.
//
// A deny match costs no visibility of the call itself. Replay still emits the span with its url,
// method, status code and both timestamps, and drops only the request and response detail.
//
// One regex per model pair, because the api owns both routes and each app posts to whichever one its
// users authenticate against.
//
// The Adyen charge routes are here for a different credential: Adyen's ACH component sends the
// payer's bank account and routing numbers in the request body in plaintext, and the response to a
// details call echoes the payment state back. Neither has a shape a value-based rule could key on —
// an account number is just digits — so refusing to capture the body is the only thing that reaches
// it.
const CREDENTIAL_BODY_URLS = [
  /\/api\/(?:agents|clients)\/(?:login|signup|set-password|set-initial-password|reset-password|verify-token)\b/i,
  /\/api\/itineraries\/[^/?]+\/adyen\/payment(?:\/details)?(?=[#/?]|$)/i,
];

// An access token that arrives as a JSON STRING VALUE rather than inside a URL. Every pattern above
// keys on URL structure, and a minted token has none: it is a bare value under a key.
//
// This exists because endpoint enumeration failed three times on one review round. A deny list of
// paths missed `loginAsUser` (the `\b` after `login` does not match), then `whoAmI` and
// `bookNowSession` (a different model), then `enquireTrip` and `enquireTrip2`. Each miss shipped a
// live token. A new endpoint that answers with a token would miss again, and nothing at the call site
// makes that visible. So the rule keys on the CREDENTIAL rather than on where it was found.
//
// The shape is exact and it is the api's own: LoopBack mints an access token id as 64 characters from
// `uid2`'s 62-character alphabet, and nothing overrides `accessTokenIdLength`. A Mongo id is 24 hex
// and a uuid carries dashes, so neither can collide. A 64-character hex digest does match, and losing
// one from a replay body costs nothing worth keeping.
//
// The Replay integration stores a network body in TWO shapes, and each needs its own rule. Measured
// in `@sentry/replay` 10.69.0 `normalizeNetworkBody`: a body that starts and ends with a brace or a
// bracket is JSON.parse'd and stored as an OBJECT, and one over the 150 KB cap is truncated and stored
// as a STRING. So `redactDeep` reaches a token either as a whole leaf value or as text inside a larger
// string, and a rule written for one shape is blind to the other.
//
// TOKEN_ONLY is the parsed case. `redactDeep` walks the object and hands each leaf here, so the token
// arrives with no quotes and no key: anchored on the whole value, it covers a field, an array element
// and a bare array alike. This is the shape that actually ships, and a rule that read the serialised
// text was measured to miss every one of them.
//
// TOKEN_QUOTED is the string case, and it earns its place on ONE shape: a body nested as an escaped
// string, where a backslash sits before the key's quote. Anchoring on the quotes rather than on a key
// is what reaches it, and the escape is optional on each side. An array element, a bare array and a key
// beginning with a digit also defeat a key anchor, but TOKEN_ONLY already answers all three, because
// each of those arrives parsed. Measured: swapping this rule for a key-anchored one turns exactly one
// case red, not four.
//
// A bare run in prose or in a stack frame carries no quotes, so it is left alone.
const TOKEN_ONLY = /^[\dA-Za-z]{64}$/;
const TOKEN_QUOTED = /(\\?")[\dA-Za-z]{64}(\\?")/g;

/**
 * @param {string} value
 * @returns {string}
 */
const redactUrlCredentials = value =>
  // A leaf that IS a token carries no delimiter to key on, so it is answered before the chain.
  TOKEN_ONLY.test(value)
    ? 'REDACTED'
    : value
        .replace(VERIFY_TOKEN, '$1$2REDACTED')
        .replace(CLIENT_ROUTE_TOKEN, '$1REDACTED')
        .replace(AGENT_ROUTE_TOKEN, '$1REDACTED')
        .replace(DIGITRIPS_PUSH_SECRET, '$1REDACTED')
        .replace(ACCESS_TOKEN_PARAM, '$1access_token=REDACTED')
        .replace(TOKEN_PARAM, '$1token=REDACTED')
        .replace(BEARER_HEADER, '$1REDACTED')
        .replace(TOKEN_QUOTED, '$1REDACTED$2');

// The subtrees of an event where a URL can appear. A whole-event walk would be simpler to state but
// it runs over the entire payload on every send, and a replay event is large; these are the places a
// URL actually reaches, so the walk stays bounded and its cost is predictable.
//
//   request      url and headers on any event
//   breadcrumbs  navigation and xhr crumbs carry the url they describe
//   urls         the pages a replay segment covers
//   spans        a span description is the method and url of the call it timed
//   contexts     serialised header maps and response bodies land here
//   extra        the same, from call sites that use setExtra
//   exception    a constructed Error puts the url in the message, so it is in the exception value
//   message      a captureMessage with a url in the text
//   logentry     a captureMessage given a template: the url sits in params, and `message` is unset
//   transaction  the event title, which for an http event is a path
const URL_BEARING = ['request', 'breadcrumbs', 'urls', 'spans', 'contexts', 'extra', 'exception', 'message', 'logentry', 'transaction'];

// Depth cap and cycle guard: this runs on the egress path, where a throw loses the report entirely.
// A prepared event is already normalised and acyclic, but beforeSend and processors also see events
// built by hand at call sites, and those carry whatever the caller put on them.
const MAX_DEPTH = 12;

function redactDeep(value, depth = 0, seen = new WeakSet()) {
  if (typeof value === 'string') return redactUrlCredentials(value);
  if (depth >= MAX_DEPTH || !value || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((entry, index) => {
      value[index] = redactDeep(entry, depth + 1, seen);
    });
    return value;
  }
  if (isPlainObject(value)) {
    forEach(value, (entry, key) => {
      value[key] = redactDeep(entry, depth + 1, seen);
    });
  }
  return value;
}

// Belongs on `addEventProcessor`, and specifically not on `beforeSend`: the client applies
// `beforeSend` while processing an event, and the Replay integration never enters that path — it
// builds its own envelope and hands it to the transport. Event processors run inside `prepareEvent`,
// which replay does call, so one processor covers error events, transaction events and the replay
// event alike, where `beforeSend` reaches only the first.
//
// This is egress-side on purpose. Sentry's own scrubbing happens after ingest, so anything it cleans
// has already crossed the wire and been written down by a third party; it is also key-name based and
// per-project, so it covers only the names it knows, only where somebody switched it on.
/**
 * @template T
 * @overload
 * @param {T} event
 * @returns {T}
 */
function redactSentryEvent(event) {
  if (!event) return event;

  URL_BEARING.forEach(key => {
    if (event[key] !== undefined) event[key] = redactDeep(event[key]);
  });

  // A header whose entire value is the credential has no URL shape to match, so the value goes
  // wholesale. A magic link IS the page URL, so it also lands in the Referer on every browser event.
  forEach(get(event, 'request.headers'), (value, name) => {
    if (typeof value !== 'string') return;
    if (CREDENTIAL_HEADER.test(name)) event.request.headers[name] = 'REDACTED';
  });

  // The same credentials again, as span attributes. A span attribute is a flat key on a data bag
  // rather than an entry in a header map, so the pass above cannot reach it, and the value is a bare
  // token with no URL shape for `redactDeep` to match. The transaction's own attributes live on
  // `contexts.trace.data`; a child span carries its own copy.
  forEach([get(event, 'contexts.trace.data'), ...(get(event, 'spans') || []).map(span => get(span, 'data'))], data => {
    if (!isPlainObject(data)) return;
    forEach(data, (value, key) => {
      if (typeof value === 'string' && CREDENTIAL_SPAN_KEYS.has(key)) data[key] = 'REDACTED';
    });
  });

  return event;
}

// Registered as the Replay integration's `beforeAddRecordingEvent`. That hook is handed only rrweb
// events of type Custom — the SDK checks the type before calling it — which is the breadcrumb and
// performanceSpan events replay synthesises. Those carry the navigation and network URLs.
//
// The rest of the recording goes through `redactRecordedUrls` instead. The split is by what the SDK
// supports: this hook is public and documented, so it keeps the events it can reach.
/**
 * @template T
 * @overload
 * @param {T} event
 * @returns {T}
 */
function redactRecordingEvent(event) {
  if (!event || !event.data) return event;
  event.data = redactDeep(event.data);
  return event;
}

// rrweb event types and mutation sources. The SDK inlines rrweb and exports neither enum, and a
// recording carries the number on the wire, so the values are matched literally.
const RECORDING_FULL_SNAPSHOT = 2;
const RECORDING_INCREMENTAL_SNAPSHOT = 3;
const RECORDING_META = 4;
const MUTATION_SOURCE = 0;

// Attribute names whose value is a URL. rrweb resolves each one against the document and writes it
// out verbatim: `transformAttribute` returns an absolute URL for these before the attribute-masking
// callback runs, so `maskAttributes` cannot reach them however it is configured.
const URL_ATTRIBUTES = ['href', 'xlink:href', 'src', 'srcset'];

function redactAttributeUrls(attributes) {
  if (!isPlainObject(attributes)) return;
  URL_ATTRIBUTES.forEach(name => {
    if (typeof attributes[name] === 'string') attributes[name] = redactUrlCredentials(attributes[name]);
  });
}

// Depth follows the recorded DOM, which rrweb has already walked to build this tree.
function redactNodeUrls(node) {
  if (!node) return;
  redactAttributeUrls(node.attributes);
  if (Array.isArray(node.childNodes)) node.childNodes.forEach(redactNodeUrls);
}

// Installed as rrweb's `packFn`, which receives every recording event. Three places in a recording
// hold a URL, and no public option reaches any of them:
//
//   Meta                  `data.href` is `window.location.href` at each full snapshot
//   FullSnapshot          every URL attribute in the serialised document
//   IncrementalSnapshot   the same attributes on nodes added or changed later
//
// A share token is a path segment or query parameter of the page URL, so a page that carries one
// puts it in all three. Custom events are left alone: `redactRecordingEvent` owns those.
//
// Only the URL attributes are visited, not every string. Text content is a separate class — the
// front ends mask it or do not — and walking a whole snapshot on every checkout is work this does
// not need to do.
/**
 * @template T
 * @overload
 * @param {T} event
 * @returns {T}
 */
function redactRecordedUrls(event) {
  if (!event || !event.data) return event;

  if (event.type === RECORDING_META) {
    if (typeof event.data.href === 'string') event.data.href = redactUrlCredentials(event.data.href);
    return event;
  }

  if (event.type === RECORDING_FULL_SNAPSHOT) {
    redactNodeUrls(event.data.node);
    return event;
  }

  if (event.type === RECORDING_INCREMENTAL_SNAPSHOT && event.data.source === MUTATION_SOURCE) {
    if (Array.isArray(event.data.adds)) event.data.adds.forEach(add => redactNodeUrls(add.node));
    if (Array.isArray(event.data.attributes)) event.data.attributes.forEach(mutation => redactAttributeUrls(mutation.attributes));
  }

  return event;
}

// `packFn` is an rrweb option that the Replay integration does not forward: it builds its recording
// options from a closed list, so the only way in is the field it builds them on. The integration
// creates that object in its constructor and hands it to the recorder later, spreading it first, so
// a write here lands before anything is recorded and survives every checkout.
//
// rrweb means `packFn` for compression and applies it to each event before the SDK's own handler
// runs. Nothing downstream reads what it returns, so returning the event mutated is transparent.
//
// Throws rather than skipping. This is the only redaction covering the recording, and an SDK upgrade
// that renames the field would otherwise ship a leak quietly; a throw fails the unit tests that
// build a real integration, before it can reach a browser.
/**
 * @template T
 * @overload
 * @param {T} replay
 * @returns {T}
 */
function installRecordedUrlRedaction(replay) {
  if (!replay || !isPlainObject(replay._recordingOptions)) {
    throw new Error('Sentry Replay integration exposes no _recordingOptions — recording URL redaction cannot be installed');
  }
  replay._recordingOptions.packFn = redactRecordedUrls;
  return replay;
}

export {
  CREDENTIAL_BODY_URLS,
  redactSentryEvent,
  redactRecordingEvent,
  redactRecordedUrls,
  installRecordedUrlRedaction,
  // Exported for a destination with no event processor to hang a walk on. New Relic's node
  // agent redacts by config and offers no beforeSend, so a caller that builds its own
  // attribute has to redact the value itself.
  redactUrlCredentials,
};
